
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Snackbar,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  GlobalStyles,
  InputAdornment,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import api from "../api";

// Optional (kalau file CSS ini memang ada)
import "../components/ProductManagementPage.css";

const emptyBatch = () => ({
  source: "pembelian",
  source_note: "",
  pabrik: "",
  tanggal_masuk: "",
  batch: "",
  stok_masuk: "",
  tanggal_kadaluarsa: "",
  keterangan: "",
});

const placeholderPhotoStyle = {
  width: 48,
  height: 48,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f2f4f7",
  color: "#94a3b8",
  fontSize: 12,
};

const normalizeUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `${window.location.protocol}${raw}`;
  if (raw.startsWith("/")) return `${window.location.origin}${raw}`;

  return `${window.location.origin}/${raw.replace(/^\/+/, "")}`;
};

const getPhotoCandidates = (product) => {
  const candidates = [];

  const photoUrl = String(product?.photo_url || "").trim();
  const photoFile = String(product?.photo || "").trim();

  if (photoUrl) {
    candidates.push(normalizeUrl(photoUrl));

    try {
      const parsed = new URL(photoUrl, window.location.origin);
      if (parsed.pathname) {
        candidates.push(`${window.location.origin}${parsed.pathname}`);
      }
    } catch (_e) {
      // ignore
    }
  }

  if (photoFile) {
    const safeFile = encodeURIComponent(photoFile).replace(/%2F/g, "/");
    candidates.push(`/storage/products/${safeFile}`);
    candidates.push(`${window.location.origin}/storage/products/${safeFile}`);
  }

  return [...new Set(candidates.filter(Boolean))];
};

function ProductPhoto({ product }) {
  const candidates = useMemo(() => getPhotoCandidates(product), [product]);

  const [srcIndex, setSrcIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrcIndex(0);
    setFailed(false);
  }, [product?.id, product?.photo, product?.photo_url]);

  if (!candidates.length || failed) {
    return <div style={placeholderPhotoStyle}>—</div>;
  }

  return (
    <img
      src={candidates[srcIndex]}
      alt={product?.name || "Foto Produk"}
      width={48}
      height={48}
      style={{ objectFit: "contain", borderRadius: 6, background: "#fff" }}
      onError={() => {
        const nextIndex = srcIndex + 1;
        if (nextIndex < candidates.length) {
          setSrcIndex(nextIndex);
        } else {
          setFailed(true);
        }
      }}
    />
  );
}

function ProductManagementPage({ userRole: _userRole }) {
  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);
  const [categoriesTree, setCategoriesTree] = useState([]);

  const [showModal, setShowModal] = useState(false);

  const [productData, setProductData] = useState({
    name: "",
    main_category_id: "",
    category_id: "",
    photo_file: null,
    photo_preview: "",
    initial_batches: [emptyBatch()],
  });

  const [formErrors, setFormErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);

  const [incomingTotals, setIncomingTotals] = useState({});

  const [q, setQ] = useState("");

  const unwrapList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.categories)) return payload.categories;
    if (Array.isArray(payload?.data?.categories)) return payload.data.categories;
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload?.items)) return payload.items;

    const asValues = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
      const vals = Object.values(obj);
      return Array.isArray(vals) && vals.length && typeof vals[0] === "object" ? vals : null;
    };

    const v1 = asValues(payload?.data);
    if (v1) return v1;

    const v2 = asValues(payload);
    if (v2) return v2;

    return [];
  };

  const normalizeToTree = (data) => {
    if (Array.isArray(data) && data.length > 0 && Object.prototype.hasOwnProperty.call(data[0], "children")) {
      const roots = (data || []).map((p) => ({
        id: Number(p.id),
        name: String(p.name ?? "").trim(),
        parent_id: p.parent_id ?? null,
        children: Array.isArray(p.children)
          ? p.children.map((c) => ({
              id: Number(c.id),
              name: String(c.name ?? "").trim(),
              parent_id: c.parent_id ?? Number(p.id),
              children: [],
            }))
          : [],
      }));

      roots.sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }));
      roots.forEach((r) =>
        (r.children || []).sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }))
      );
      return roots;
    }

    const list = Array.isArray(data) ? data : [];
    const map = new Map();

    list.forEach((c) => {
      const id = Number(c.id ?? c.category_id ?? c.categoryId);
      if (!id) return;
      map.set(id, {
        id,
        name: String(c.name ?? c.nama ?? "").trim(),
        parent_id: c.parent_id ?? c.parentId ?? null,
        children: [],
      });
    });

    const roots = [];
    map.forEach((node) => {
      const pidRaw = node.parent_id;
      const pid = pidRaw === null || pidRaw === undefined || pidRaw === "" ? null : Number(pidRaw);
      node.parent_id = Number.isFinite(pid) ? pid : null;

      if (node.parent_id && map.has(node.parent_id)) {
        map.get(node.parent_id).children.push(node);
      } else {
        roots.push(node);
      }
    });

    roots.sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }));
    roots.forEach((r) =>
      (r.children || []).sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { sensitivity: "base" }))
    );
    return roots;
  };

  const flattenTree = (tree) => {
    const out = [];
    (tree || []).forEach((p) => {
      out.push({
        id: Number(p.id),
        name: String(p.name || "").trim(),
        parent_id: null,
        parent_name: null,
      });
      (p.children || []).forEach((c) => {
        out.push({
          id: Number(c.id),
          name: String(c.name || "").trim(),
          parent_id: Number(p.id),
          parent_name: String(p.name || "").trim(),
        });
      });
    });
    return out.filter((x) => Number.isFinite(x.id) && x.name);
  };

  const normalizeText = (s = "") =>
    String(s)
      .toLowerCase()
      .replace(/&/g, " dan ")
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchIncomingTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      const list = unwrapList(res.data).map((p) => ({
        id: p.id,
        name: p.name,
        category_id: p.category_id ?? p.categoryId ?? null,
        category_name: p.category?.name ?? p.category_name ?? p.categoryName ?? null,
        stok_awal: Number(p.stok_awal ?? p.stock_awal ?? p.stokAwal ?? p.stock ?? 0),
        jumlah_stok: p.jumlah_stok ?? p.total_stock ?? p.jumlahStok ?? p.stock_total ?? null,
        photo: p.photo || null,
        photo_url: p.photo_url || null,
      }));
      setProducts(list);
    } catch (e) {
      console.error("Gagal mengambil data produk:", e);
      setProducts([]);
      setSnackbarMessage("Gagal mengambil data produk");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchCategories = async () => {
    try {
      let res;
      try {
        res = await api.get("/categories", { params: { mode: "tree" } });
      } catch (_e) {
        res = await api.get("/categories");
      }

      const tree = normalizeToTree(res.data);
      const flat = flattenTree(tree);

      setCategoriesTree(tree);
      setCategories(flat);
    } catch (e) {
      console.error("Gagal mengambil data kategori:", e);
      setCategories([]);
      setCategoriesTree([]);
      setSnackbarMessage("Gagal mengambil data kategori");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fetchIncomingTotals = async () => {
    try {
      const res = await api.get("/incoming_products", { params: { all: 1 } });
      const list = unwrapList(res.data);

      const totals = {};
      for (const row of list) {
        if (String(row.source || "").toLowerCase() === "stok_awal") continue;

        const pid = Number(row.product_id ?? row?.product?.id ?? 0);
        if (!pid) continue;

        const qty = Number(row.stok_masuk ?? row.qty ?? row.quantity ?? 0);
        totals[pid] = (totals[pid] || 0) + (Number.isFinite(qty) ? qty : 0);
      }

      setIncomingTotals(totals);
    } catch (e) {
      console.error("Gagal mengambil data produk masuk (untuk akumulasi stok):", e);
      setIncomingTotals({});
    }
  };

  useEffect(() => {
    if (!showModal) {
      if (productData.photo_preview) URL.revokeObjectURL(productData.photo_preview);
      setProductData({
        name: "",
        main_category_id: "",
        category_id: "",
        photo_file: null,
        photo_preview: "",
        initial_batches: [emptyBatch()],
      });
      setFormErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    if (productData.main_category_id) return;

    const first =
      (categoriesTree || []).find((p) => (p.children || []).length > 0) || (categoriesTree || [])[0];

    if (first?.id) {
      setProductData((prev) => ({
        ...prev,
        main_category_id: Number(first.id),
        category_id: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, categoriesTree]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "main_category_id") {
      const v = value === "" ? "" : Number(value);
      setProductData((prev) => ({
        ...prev,
        main_category_id: v,
        category_id: "",
      }));
      if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
      if (formErrors.category_id) setFormErrors((prev) => ({ ...prev, category_id: "" }));
      return;
    }

    if (name === "category_id") {
      const v = value === "" ? "" : Number(value);
      setProductData((prev) => ({ ...prev, [name]: v }));
      if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setProductData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const setBatch = (idx, patch) => {
    setProductData((prev) => ({
      ...prev,
      initial_batches: prev.initial_batches.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));
  };

  const addBatch = () => {
    setProductData((prev) => ({
      ...prev,
      initial_batches: [...prev.initial_batches, emptyBatch()],
    }));
  };

  const removeBatch = (idx) => {
    setProductData((prev) => ({
      ...prev,
      initial_batches:
        prev.initial_batches.length <= 1 ? prev.initial_batches : prev.initial_batches.filter((_, i) => i !== idx),
    }));
  };

  const totalStokAwal = useMemo(() => {
    return (productData.initial_batches || []).reduce((acc, b) => acc + (Number(b.stok_masuk) || 0), 0);
  }, [productData.initial_batches]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (productData.photo_preview) URL.revokeObjectURL(productData.photo_preview);

    setProductData((prev) => ({
      ...prev,
      photo_file: file,
      photo_preview: file ? URL.createObjectURL(file) : "",
    }));
    if (formErrors.photo) setFormErrors((prev) => ({ ...prev, photo: "" }));
  };

  const validateAddProduct = () => {
    const errors = {};
    if (!productData.name.trim()) errors.name = "Nama produk wajib diisi";
    if (productData.main_category_id === "" || productData.main_category_id === null) {
      errors.main_category_id = "Pilih kategori utama";
    }
    if (productData.category_id === "" || productData.category_id === null) {
      errors.category_id = "Pilih sub kategori";
    }

    const batches = productData.initial_batches || [];
    if (!batches.length) {
      errors.initial_batches = "Minimal 1 batch stok awal.";
      return errors;
    }

    batches.forEach((b, i) => {
      const qty = Number(b.stok_masuk) || 0;
      if (qty < 1) errors[`batch_qty_${i}`] = `Stok Masuk batch #${i + 1} wajib >= 1`;
      if (!b.tanggal_masuk) errors[`batch_tgl_${i}`] = `Tanggal Masuk batch #${i + 1} wajib diisi`;
      if (!b.source) errors[`batch_src_${i}`] = `Sumber batch #${i + 1} wajib dipilih`;
      if (b.source === "transfer_masuk" && !String(b.source_note || "").trim()) {
        errors[`batch_note_${i}`] = `Keterangan sumber wajib untuk Transfer masuk (batch #${i + 1})`;
      }
      if (b.source === "lainnya" && !String(b.source_note || "").trim()) {
        errors[`batch_note_${i}`] = `Isi keterangan sumber untuk "Lainnya" (batch #${i + 1})`;
      }
    });

    return errors;
  };

  const handleFormSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const errors = validateAddProduct();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const batches = (productData.initial_batches || []).map((b) => ({
      source: String(b.source || ""),
      source_note: String(b.source_note || ""),
      pabrik: String(b.pabrik || ""),
      tanggal_masuk: b.tanggal_masuk,
      batch: String(b.batch || ""),
      stok_masuk: Number(b.stok_masuk) || 0,
      tanggal_kadaluarsa: b.tanggal_kadaluarsa || null,
      keterangan: String(b.keterangan || ""),
    }));

    const fd = new FormData();
    fd.append("name", productData.name.trim());
    fd.append("category_id", String(Number(productData.category_id)));
    fd.append("initial_batches", JSON.stringify(batches));
    fd.append("stok_awal", String(totalStokAwal));
    fd.append("jumlah_stok", String(totalStokAwal));
    fd.append("stock", String(totalStokAwal));

    if (productData.photo_file) {
      fd.append("photo", productData.photo_file);
    }

    try {
      await api.post("/products", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbarMessage(`Produk '${productData.name}' berhasil ditambahkan`);
      setSnackbarSeverity("success");
      setShowModal(false);

      await fetchProducts();
      await fetchCategories();
      await fetchIncomingTotals();

      setSnackbarOpen(true);
    } catch (error) {
      if (error?.response?.status === 422 && error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        console.error("Gagal menyimpan:", error);
        setSnackbarMessage(error?.response?.data?.message || "Terjadi kesalahan saat menyimpan");
      }
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddProductClick = async () => {
    if (!categoriesTree.length) {
      await fetchCategories();
    }
    setShowModal(true);
  };

  const handleDeleteProductClick = (product) => {
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/products/${productToDelete.id}`);
      setSnackbarMessage(`Produk '${productToDelete.name}' berhasil dihapus`);
      setSnackbarSeverity("success");

      await fetchProducts();
      await fetchIncomingTotals();
    } catch (e) {
      console.error("Gagal hapus:", e);
      setSnackbarMessage("Gagal menghapus produk");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
    setShowDeleteConfirm(false);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSnackbarClose = (_e, reason) => {
    if (reason !== "clickaway") setSnackbarOpen(false);
  };

  const categoryById = useMemo(() => {
    const m = new Map();
    (categories || []).forEach((c) => {
      m.set(Number(c.id), c);
    });
    return m;
  }, [categories]);

  const getCategoryParts = (category_id, fallbackName = null) => {
    const idNum = Number(category_id);
    const cat = categoryById.get(idNum);

    if (cat) {
      if (cat.parent_name) return { main: cat.parent_name, sub: cat.name };
      return { main: cat.name, sub: "-" };
    }

    return { main: "LAINNYA", sub: fallbackName || "Tidak Diketahui" };
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const handlePrintTable = (mainName, list) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup diblokir browser. Izinkan popup untuk mencetak.");
      return;
    }

    const now = new Date();
    const tanggal = now.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const jam = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

    const rowsHtml =
      (list || []).length > 0
        ? (list || [])
            .map((p, i) => {
              const parts = getCategoryParts(p.category_id, p.category_name);
              const stokAwal = Number(p.stok_awal ?? 0).toLocaleString("id-ID");
              const jumlah = Number(p.jumlah_stok ?? 0).toLocaleString("id-ID");

              return `
                <tr>
                  <td style="border:1px solid #000; padding:6px; text-align:center;">${i + 1}</td>
                  <td style="border:1px solid #000; padding:6px; text-align:center;">${escapeHtml(p.id)}</td>
                  <td style="border:1px solid #000; padding:6px;">${escapeHtml(p.name)}</td>
                  <td style="border:1px solid #000; padding:6px;">${escapeHtml(parts.sub)}</td>
                  <td style="border:1px solid #000; padding:6px; text-align:right;">${stokAwal}</td>
                  <td style="border:1px solid #000; padding:6px; text-align:right;">${jumlah}</td>
                </tr>
              `;
            })
            .join("")
        : `
          <tr>
            <td colspan="6" style="border:1px solid #000; padding:10px; text-align:center;">
              Tidak ada data pada tabel ini.
            </td>
          </tr>
        `;

    const title = `DAFTAR PRODUK - ${mainName}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="id">
      <head>
        <meta charset="UTF-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: "Times New Roman", serif; font-size: 11pt; margin: 15mm; }
          .kop { text-align:left; margin-bottom: 8px; }
          .kop img { max-width: 260mm; height:auto; }
          h1 { font-size: 14pt; text-align:center; margin: 0; }
          .subtitle { text-align:center; font-size: 10pt; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { border: 1px solid #000; padding: 6px; text-align:center; font-weight:bold; background:#f2f2f2; }
          td { border: 1px solid #000; padding: 6px; }
          .meta { margin-top: 10px; font-size: 9pt; }
          @page { size: A4 portrait; margin: 15mm; }
        </style>
      </head>
      <body>
        <div class="kop">
          <img src="/kop.png" alt="Kop" />
        </div>

        <h1>${escapeHtml(title)}</h1>
        <div class="subtitle">Sistem Informasi Pencatatan Data Stok (SIPEDAS)</div>

        <table>
          <thead>
            <tr>
              <th style="width:7%;">No</th>
              <th style="width:10%;">ID</th>
              <th>Nama Produk</th>
              <th style="width:20%;">Sub Kategori</th>
              <th style="width:13%;">Stok Awal</th>
              <th style="width:13%;">Jumlah Stok</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="meta">
          Dicetak pada: ${tanggal} pukul ${jam} • Total: <b>${(list || []).length}</b> produk
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const computedProducts = useMemo(() => {
    return (products || []).map((p) => {
      const stokAwal = Number(p.stok_awal ?? 0);
      const incoming = Number(incomingTotals?.[p.id] ?? 0);

      const jumlahFromApi = p.jumlah_stok === null || p.jumlah_stok === undefined ? null : Number(p.jumlah_stok);
      const jumlah = Number.isFinite(jumlahFromApi) ? jumlahFromApi : stokAwal + incoming;

      return {
        ...p,
        stok_awal: Number.isFinite(stokAwal) ? stokAwal : 0,
        jumlah_stok: Number.isFinite(jumlah) ? jumlah : 0,
      };
    });
  }, [products, incomingTotals]);

  const filteredProducts = useMemo(() => {
    const qq = normalizeText(q);
    if (!qq) return computedProducts;

    return (computedProducts || []).filter((p) => {
      const parts = getCategoryParts(p.category_id, p.category_name);
      return (
        normalizeText(p.name).includes(qq) ||
        normalizeText(parts.main).includes(qq) ||
        normalizeText(parts.sub).includes(qq) ||
        normalizeText(p.category_name || "").includes(qq)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedProducts, q, categoryById]);

  const mainOrderFromDb = useMemo(() => {
    const mains = (categoriesTree || []).map((p) => String(p.name || "").trim()).filter(Boolean);

    const seen = new Set();
    const unique = [];
    for (const n of mains) {
      const key = normalizeText(n);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(n);
      }
    }
    return unique;
  }, [categoriesTree]);

  const mainNameMap = useMemo(() => {
    const m = new Map();
    (mainOrderFromDb || []).forEach((name) => {
      m.set(normalizeText(name), name);
    });
    return m;
  }, [mainOrderFromDb]);

  const groupedByMain = useMemo(() => {
    const groups = {};

    (mainOrderFromDb || []).forEach((main) => {
      groups[main] = [];
    });

    groups["LAINNYA"] = [];

    (filteredProducts || []).forEach((p) => {
      const parts = getCategoryParts(p.category_id, p.category_name);
      const mainName = parts.main || "LAINNYA";

      const canonical = mainNameMap.get(normalizeText(mainName));
      if (canonical) groups[canonical].push(p);
      else groups["LAINNYA"].push(p);
    });

    return groups;
  }, [filteredProducts, mainOrderFromDb, mainNameMap, categoryById]);

  const openDetail = (product) => {
    setDetailRows([]);
    setDetailProduct(product);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailProduct(null);
    setDetailRows([]);
    setDetailLoading(false);
  };

  const formatTanggal = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const mapSumber = (src, note) => {
    const s = String(src || "").toLowerCase();
    let label = "-";

    if (s === "stok_awal") label = "Stok Awal";
    else if (s === "pembelian") label = "Pembelian";
    else if (s === "transfer_masuk") label = "Transfer Masuk";
    else if (s === "hibah") label = "Hibah";
    else if (s === "apbn") label = "APBN";
    else if (s === "apbd") label = "APBD";
    else if (s === "lainnya") label = "Lainnya";
    else label = src || "-";

    const n = (note || "").trim();
    return n ? `${label} (${n})` : label;
  };

  useEffect(() => {
    if (!detailOpen || !detailProduct?.id) return;

    let cancelled = false;

    (async () => {
      setDetailLoading(true);
      try {
        const res = await api.get(`/products/${detailProduct.id}/batch-details`);
        const list = unwrapList(res.data);
        if (!cancelled) setDetailRows(list);
      } catch (e) {
        console.error("Gagal memuat batch-details:", e);
        if (!cancelled) setDetailRows([]);
        setSnackbarMessage("Gagal memuat detail batch produk.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen, detailProduct?.id]);

  const renderTable = (mainName, list) => (
    <Box sx={{ mt: 2, minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {`Tabel ${mainName}`}
          </Typography>
          <Chip size="small" label={`${(list || []).length} Produk`} />
        </Box>

        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => handlePrintTable(mainName, list)}>
          Cetak Tabel
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxHeight: { xs: "65vh", md: "55vh" },
          minHeight: 240,
          overflow: "auto",
          borderRadius: 2,
          border: "1px solid #edf1f5",
          scrollbarGutter: "stable both-edges",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" width={90}>
                Foto
              </TableCell>
              <TableCell width={60}>ID</TableCell>
              <TableCell>Nama Produk</TableCell>
              <TableCell>Sub Kategori</TableCell>
              <TableCell align="right" width={110}>
                Stok Awal
              </TableCell>
              <TableCell align="right" width={120}>
                Jumlah Stok
              </TableCell>
              <TableCell align="center" width={220}>
                Aksi
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(list || []).map((product) => {
              const parts = getCategoryParts(product.category_id, product.category_name);

              return (
                <TableRow key={product.id} hover>
                  <TableCell align="center">
                    <ProductPhoto product={product} />
                  </TableCell>

                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{parts.sub}</TableCell>
                  <TableCell align="right">{Number(product.stok_awal ?? 0).toLocaleString("id-ID")}</TableCell>
                  <TableCell align="right">{Number(product.jumlah_stok ?? 0).toLocaleString("id-ID")}</TableCell>

                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={() => openDetail(product)}
                      sx={{ mr: 1 }}
                    >
                      Detail
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteProductClick(product)}
                    >
                      Hapus
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {(!list || list.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  Belum ada data pada tabel ini.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const mainOptions = useMemo(() => {
    return (categoriesTree || [])
      .map((p) => ({ id: Number(p.id), name: String(p.name || "").trim(), children: p.children || [] }))
      .filter((p) => Number.isFinite(p.id) && p.name);
  }, [categoriesTree]);

  const selectedMain = useMemo(() => {
    const idNum = Number(productData.main_category_id);
    return mainOptions.find((m) => Number(m.id) === idNum) || null;
  }, [mainOptions, productData.main_category_id]);

  const subOptions = useMemo(() => {
    if (!selectedMain) return [];
    return (selectedMain.children || [])
      .map((c) => ({ id: Number(c.id), name: String(c.name || "").trim() }))
      .filter((c) => Number.isFinite(c.id) && c.name);
  }, [selectedMain]);

  const categoryMainLabelId = "pm-main-category-label";
  const categorySubLabelId = "pm-sub-category-label";

  return (
    <>
      <GlobalStyles
        styles={{
          html: { height: "auto", overflowY: "auto" },
          body: { height: "auto", overflowY: "auto" },
        }}
      />

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: { xs: 2, md: 3 },
          mb: 6,
          px: { xs: 2, md: 3 },
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Manajemen Produk
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <TextField
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama produk / kategori..."
              sx={{ minWidth: { xs: "100%", sm: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQ("")} aria-label="clear">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
              Cetak Daftar Produk
            </Button>

            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProductClick}>
              Tambah Produk Baru
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {(mainOrderFromDb || []).length === 0 ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Kategori utama belum termuat / belum ada kategori utama.
          </Typography>
        ) : (
          (mainOrderFromDb || []).map((mainName) => renderTable(mainName, groupedByMain?.[mainName] || []))
        )}

        {(groupedByMain?.["LAINNYA"] || []).length > 0 && renderTable("LAINNYA", groupedByMain["LAINNYA"])}

        <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="md">
          <DialogTitle>
            Detail Batch — {detailProduct?.name || "-"} (ID: {detailProduct?.id || "-"})
          </DialogTitle>
          <DialogContent dividers>
            {detailLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #edf1f5", overflow: "auto" }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={220}>Sumber</TableCell>
                      <TableCell width={200}>Batch</TableCell>
                      <TableCell align="right" width={120}>
                        Stok Masuk
                      </TableCell>
                      <TableCell align="right" width={120}>
                        Stok Keluar
                      </TableCell>
                      <TableCell align="right" width={140}>
                        Stok Sisa Batch
                      </TableCell>
                      <TableCell width={160}>Tanggal Masuk</TableCell>
                      <TableCell width={180}>Tanggal Kadaluarsa</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailRows || []).map((row) => {
                      const masuk = Number(row.stok_masuk ?? 0);
                      const keluar = Number(row.stok_keluar ?? 0);
                      const sisa = Number.isFinite(Number(row.stok_sisa_batch))
                        ? Number(row.stok_sisa_batch)
                        : Math.max(0, masuk - keluar);

                      return (
                        <TableRow key={row.id}>
                          <TableCell>{mapSumber(row.source, row.source_note)}</TableCell>
                          <TableCell>{row.batch || "-"}</TableCell>
                          <TableCell align="right">{Number.isFinite(masuk) ? masuk : 0}</TableCell>
                          <TableCell align="right">{Number.isFinite(keluar) ? keluar : 0}</TableCell>
                          <TableCell align="right">{Number.isFinite(sisa) ? sisa : 0}</TableCell>
                          <TableCell>{formatTanggal(row.tanggal_masuk ?? row.created_at)}</TableCell>
                          <TableCell>{formatTanggal(row.tanggal_kadaluarsa)}</TableCell>
                        </TableRow>
                      );
                    })}

                    {(!detailRows || detailRows.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          Belum ada batch untuk produk ini.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDetail}>Tutup</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showModal} onClose={handleCloseModal} fullWidth maxWidth="md">
          <DialogTitle>Tambah Produk Baru</DialogTitle>
          <DialogContent dividers>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>Informasi Produk</Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                <TextField
                  name="name"
                  label="Nama Produk"
                  fullWidth
                  value={productData.name}
                  onChange={handleInputChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  size="small"
                />

                <FormControl fullWidth size="small" error={!!formErrors.main_category_id}>
                  <InputLabel id={categoryMainLabelId}>Kategori Utama</InputLabel>
                  <Select
                    labelId={categoryMainLabelId}
                    name="main_category_id"
                    label="Kategori Utama"
                    value={productData.main_category_id === "" ? "" : Number(productData.main_category_id)}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">
                      <em>-- Pilih Kategori Utama --</em>
                    </MenuItem>

                    {mainOptions.map((m) => (
                      <MenuItem key={String(m.id)} value={Number(m.id)}>
                        {m.name}
                      </MenuItem>
                    ))}

                    {!mainOptions.length && (
                      <MenuItem disabled value="__empty_main">
                        (Kategori utama belum termuat)
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" error={!!formErrors.category_id}>
                  <InputLabel id={categorySubLabelId}>Sub Kategori</InputLabel>
                  <Select
                    labelId={categorySubLabelId}
                    name="category_id"
                    label="Sub Kategori"
                    value={productData.category_id === "" ? "" : Number(productData.category_id)}
                    onChange={handleInputChange}
                    disabled={!productData.main_category_id}
                    MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                  >
                    <MenuItem value="">
                      <em>-- Pilih Sub Kategori --</em>
                    </MenuItem>

                    {subOptions.map((s) => (
                      <MenuItem key={String(s.id)} value={Number(s.id)}>
                        {s.name}
                      </MenuItem>
                    ))}

                    {productData.main_category_id && subOptions.length === 0 && (
                      <MenuItem disabled value="__empty_sub">
                        (Belum ada sub kategori untuk kategori utama ini)
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Foto (opsional)
                  </Typography>
                  <Button component="label" variant="outlined">
                    Pilih Foto
                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                  </Button>

                  {productData.photo_preview && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={productData.photo_preview}
                        alt="Preview"
                        width={96}
                        height={96}
                        style={{ objectFit: "contain", borderRadius: 8, background: "#fff" }}
                      />
                    </Box>
                  )}

                  {formErrors.photo && (
                    <Typography variant="caption" color="error" display="block">
                      {formErrors.photo}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>Stok Awal (Bisa Banyak Batch)</Typography>

                <Button variant="outlined" startIcon={<AddIcon />} onClick={addBatch}>
                  Tambah Batch
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary">
                * Stok Awal (kolom products.stok_awal) adalah akumulasi seluruh stok dari batch yang diinput di sini.
              </Typography>

              <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip label={`Total Stok Awal: ${totalStokAwal.toLocaleString("id-ID")}`} />
              </Box>

              <Divider sx={{ my: 2 }} />

              {(productData.initial_batches || []).map((b, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontWeight: 800 }}>Batch #{idx + 1}</Typography>

                    <IconButton
                      color="error"
                      onClick={() => removeBatch(idx)}
                      disabled={(productData.initial_batches || []).length <= 1}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <FormControl fullWidth size="small" error={!!formErrors[`batch_src_${idx}`]}>
                      <InputLabel>Sumber</InputLabel>
                      <Select label="Sumber" value={b.source} onChange={(e) => setBatch(idx, { source: e.target.value })}>
                        <MenuItem value="pembelian">Pembelian</MenuItem>
                        <MenuItem value="hibah">Hibah</MenuItem>
                        <MenuItem value="apbn">APBN</MenuItem>
                        <MenuItem value="apbd">APBD</MenuItem>
                        <MenuItem value="transfer_masuk">Transfer Masuk</MenuItem>
                        <MenuItem value="lainnya">Lainnya</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Keterangan Sumber"
                      value={b.source_note}
                      onChange={(e) => setBatch(idx, { source_note: e.target.value })}
                      error={!!formErrors[`batch_note_${idx}`]}
                      helperText={formErrors[`batch_note_${idx}`]}
                      placeholder={b.source === "transfer_masuk" ? "wajib untuk Transfer Masuk" : "opsional"}
                      fullWidth
                    />
                  </Box>

                  <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      size="small"
                      label="Pabrik"
                      value={b.pabrik}
                      onChange={(e) => setBatch(idx, { pabrik: e.target.value })}
                      fullWidth
                    />

                    <TextField
                      size="small"
                      label="Batch"
                      value={b.batch}
                      onChange={(e) => setBatch(idx, { batch: e.target.value })}
                      fullWidth
                    />

                    <TextField
                      size="small"
                      label="Tanggal Masuk"
                      type="date"
                      value={b.tanggal_masuk}
                      onChange={(e) => setBatch(idx, { tanggal_masuk: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors[`batch_tgl_${idx}`]}
                      helperText={formErrors[`batch_tgl_${idx}`]}
                      fullWidth
                    />

                    <TextField
                      size="small"
                      label="Tanggal Kadaluarsa"
                      type="date"
                      value={b.tanggal_kadaluarsa}
                      onChange={(e) => setBatch(idx, { tanggal_kadaluarsa: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />

                    <TextField
                      size="small"
                      label="Stok Masuk (Qty)"
                      type="number"
                      inputProps={{ min: 1, step: 1 }}
                      value={b.stok_masuk}
                      onChange={(e) => setBatch(idx, { stok_masuk: String(e.target.value).replace(/[^\d]/g, "") })}
                      error={!!formErrors[`batch_qty_${idx}`]}
                      helperText={formErrors[`batch_qty_${idx}`]}
                      fullWidth
                    />

                    <Box />

                    <TextField
                      size="small"
                      label="Keterangan"
                      value={b.keterangan}
                      onChange={(e) => setBatch(idx, { keterangan: e.target.value })}
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{ gridColumn: "1 / -1" }}
                    />
                  </Box>
                </Paper>
              ))}

              {formErrors.initial_batches && (
                <Typography variant="caption" color="error">
                  {formErrors.initial_batches}
                </Typography>
              )}
            </Paper>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Batal</Button>
            <Button onClick={handleFormSubmit} variant="contained">
              Simpan
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>Konfirmasi Hapus</DialogTitle>
          <DialogContent>
            <Typography>Yakin ingin menghapus produk '{productToDelete?.name}'?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)}>Batal</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Hapus
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}

export default ProductManagementPage;